/**
 * analysisLayouts.ts
 * 
 * Configuración de layouts específicos por tipo de análisis
 * Define qué campos mostrar en cada sección del PDF según el tipo de análisis
 * 
 * Basado en: Formato Datos Salida por análisis.json
 */

export interface AnalysisLayout {
  /** Campos para la cabecera (información básica de la muestra) */
  header: {
    leftColumn: string[];
    rightColumn: string[];
  };
  /** Campos para la sección de preparación y contaje */
  preparation: {
    leftColumn: string[];
    rightColumn: string[];
  };
  /** Indica si tiene resultados duales (BT/BR, Sr89/Sr90) */
  hasDualResults?: boolean;
}

/**
 * Mapeo de códigos de análisis a sus layouts específicos
 */
export const analysisLayouts: Record<string, AnalysisLayout> = {
  // ANÁLISIS DE TRITIO EN AGUA (H3-A)
  'H3-A': {
    header: {
      leftColumn: ['Instalación', 'Tipo de muestra', 'Análisis compartido', 'Ref. Cliente'],
      rightColumn: ['Acumulado', 'Motivo', 'Punto de muestreo', 'Nº muestras']
    },
    preparation: {
      leftColumn: [
        'Analista',
        'Fecha y hora inicio toma de muestra',
        'Fecha y hora fin de toma de muestra',
        'Cantidad de muestra',
        'Fecha de contaje',
        'TSIE blanco',
        'TSIE muestra',
        'Tiempo contaje blanco'
      ],
      rightColumn: [
        'Laboratorio',
        'Unidades',
        'Equipo de contaje',
        'Procedimiento',
        'Tiempo contaje muestra',
        'cpm muestra final',
        'cpm blanco final'
      ]
    }
  },

  // ANÁLISIS DE TRITIO EN VAPOR DE AGUA (H3-V)
  'H3-V': {
    header: {
      leftColumn: ['Instalación', 'Tipo de muestra', 'Análisis compartido', 'Ref. Cliente'],
      rightColumn: ['Acumulado', 'Motivo', 'Punto de muestreo', 'Nº muestras']
    },
    preparation: {
      leftColumn: [
        'Analista',
        'Fecha y hora inicio toma de muestra',
        'Fecha y hora fin de toma de muestra',
        'Volumen de aire analizado (m3)',
        'Fecha de contaje',
        'TSIE blanco',
        'TSIE muestra',
        'Tiempo contaje blanco'
      ],
      rightColumn: [
        'Laboratorio',
        'Unidades',
        'Equipo de contaje',
        'Procedimiento',
        'Tiempo contaje muestra',
        'cpm muestra final',
        'cpm blanco final'
      ]
    }
  },

  // ANÁLISIS DE NÍQUEL 63
  'Ni63': {
    header: {
      leftColumn: ['Instalación', 'Tipo de muestra', 'Análisis compartido', 'Ref. Cliente'],
      rightColumn: ['Acumulado', 'Motivo', 'Punto de muestreo', 'Nº muestras']
    },
    preparation: {
      leftColumn: [
        'Analista',
        'Fecha y hora inicio toma de muestra',
        'Fecha y hora fin de toma de muestra',
        'Cantidad de muestra',
        'Fecha de contaje',
        'TSIE blanco',
        'TSIE muestra',
        'Tiempo contaje blanco',
        'cpm quimioluminiscencia blanco.',
        'cpm quimioluminiscencia sample.',
        'cpm muestra brutas',
        'cpm blanco brutas',
      ],
      rightColumn: [
        'Laboratorio',
        'Unidades',
        'Equipo de contaje',
        'Procedimiento',
        'Tiempo contaje muestra',
        'Cantidad masa vial',
        'Rendimiento'
      ]
    }
  },

  // ANÁLISIS DE HIERRO 55
  'Fe55': {
    header: {
      leftColumn: ['Instalación', 'Tipo de muestra', 'Análisis compartido', 'Ref. Cliente'],
      rightColumn: ['Acumulado', 'Motivo', 'Punto de muestreo', 'Nº muestras']
    },
    preparation: {
      leftColumn: [
        'Analista',
        'Fecha y hora inicio toma de muestra',
        'Fecha y hora fin de toma de muestra',
        'Cantidad de muestra',
        'Fecha de contaje',
        'TSIE blanco',
        'TSIE muestra',
        'Tiempo contaje blanco',
        'cpm quimioluminiscencia blanco.',
        'cpm quimioluminiscencia sample.',
        'cpm muestra brutas',
        'cpm blanco brutas',
      ],
      rightColumn: [
        'Laboratorio',
        'Unidades',
        'Equipo de contaje',
        'Detector',
        'Procedimiento',
        'Tiempo contaje muestra',
        'Cantidad masa vial',
        'Rendimiento'
      ]
    }
  },

  // ANÁLISIS DE I-131
  'I131': {
    header: {
      leftColumn: ['Instalación', 'Tipo de muestra', 'Análisis compartido', 'Ref. Cliente'],
      rightColumn: ['Acumulado', 'Motivo', 'Punto de muestreo', 'Nº muestras']
    },
    preparation: {
      leftColumn: [
        'Analista',
        'Fecha y hora inicio toma de muestra',
        'Fecha y hora fin de toma de muestra',
        'Cantidad de muestra',
        'Fecha de contaje',
        'Cpm brutas de la muestra',
        'Código fondo',
        'Tiempo contaje muestra'
      ],
      rightColumn: [
        'Laboratorio',
        'Unidades',
        'Equipo de contaje',
        'Detector',
        'Procedimiento',
        'Peso precipitado',
        'Rendimiento',
        'Relación Peso Húmedo/Seco'
      ]
    }
  },

  // ANÁLISIS DE SR89 Y SR90
  'Sr89': {
    header: {
      leftColumn: ['Instalación', 'Tipo de muestra', 'Análisis compartido', 'Ref. Cliente'],
      rightColumn: ['Acumulado', 'Motivo', 'Punto de muestreo', 'Nº muestras']
    },
    preparation: {
      leftColumn: [
        'Analista',
        'Fecha y hora inicio toma de muestra',
        'Fecha y hora fin de toma de muestra',
        'Cantidad de muestra',
        'Fecha de contaje',
        'Cpm brutas muestra 1º Contaje',
        'Cpm brutas muestra 2º contaje',
        'Código fondo',
        'Tiempo contaje muestra',
        'Variación t1'
      ],
      rightColumn: [
        'Laboratorio',
        'Unidades',
        'Equipo de contaje',
        'Detector',
        'Procedimiento',
        'Peso precipitado (mg)',
        'Rendimiento cálculo',
        'Tiempo de muestreo',
        'Fecha y h. final muestreo-Fec y h. precipitado',
        'Variación t2',
        'Relación Peso Húmedo/Seco'
      ]
    },
    hasDualResults: true
  },

  'Sr90': {
    header: {
      leftColumn: ['Instalación', 'Tipo de muestra', 'Análisis compartido', 'Ref. Cliente'],
      rightColumn: ['Acumulado', 'Motivo', 'Punto de muestreo', 'Nº muestras']
    },
    preparation: {
      leftColumn: [
        'Analista',
        'Fecha y hora inicio toma de muestra',
        'Fecha y hora fin de toma de muestra',
        'Cantidad de muestra',
        'Fecha de contaje',
        'Cpm brutas muestra 1º Contaje',
        'Cpm brutas muestra 2º contaje',
        'Código fondo',
        'Tiempo contaje muestra',
        'Variación t1'
      ],
      rightColumn: [
        'Laboratorio',
        'Unidades',
        'Equipo de contaje',
        'Detector',
        'Procedimiento',
        'Peso precipitado (mg)',
        'Rendimiento cálculo',
        'Tiempo de muestreo',
        'Fecha y h. final muestreo-Fec y h. precipitado',
        'Variación t2',
        'Relación Peso Húmedo/Seco'
      ]
    },
    hasDualResults: true
  },

  // ANÁLISIS DE BPP (Beta Partículas de Polvo)
  'BPP': {
    header: {
      leftColumn: ['Instalación', 'Tipo de muestra', 'Análisis compartido', 'Ref. Cliente'],
      rightColumn: ['Acumulado', 'Motivo', 'Punto de muestreo', 'Nº muestras']
    },
    preparation: {
      leftColumn: [
        'Analista',
        'Fecha y hora inicio toma de muestra',
        'Fecha y hora fin de toma de muestra',
        'Cantidad de muestra',
        'Fecha de contaje',
        'cpm brutas de la muestra en ventana alfa',
        'cpm brutas de la muestra en ventana beta',
        'Tiempo contaje muestra'
      ],
      rightColumn: [
        'Laboratorio',
        'Unidades',
        'Equipo de contaje',
        'Detector',
        'Procedimiento',
        'Código de fondo alfa',
        'Código de fondo beta',
        'Relación Peso Húmedo/Seco'
      ]
    }
  },

  // ANÁLISIS DE AT (Alfa Total)
  'AT': {
    header: {
      leftColumn: ['Instalación', 'Tipo de muestra', 'Análisis compartido', 'Ref. Cliente'],
      rightColumn: ['Acumulado', 'Motivo', 'Punto de muestreo', 'Nº muestras']
    },
    preparation: {
      leftColumn: [
        'Analista',
        'Fecha y hora inicio toma de muestra',
        'Fecha y hora fin de toma de muestra',
        'Tipo de Preparación',
        'Cantidad de muestra',
        'Fecha de contaje',
        'cpm brutas de la muestra',
        'Tiempo contaje muestra'
      ],
      rightColumn: [
        'Laboratorio',
        'Unidades',
        'Equipo de contaje',
        'Detector',
        'Procedimiento',
        'Código de fondo alfa',
        'Relación Peso Húmedo/Seco'
      ]
    }
  },

  // ANÁLISIS DE AT EN PARTÍCULAS DE POLVO (AT-PP)
  'AT-PP': {
    header: {
      leftColumn: ['Instalación', 'Tipo de muestra', 'Análisis compartido', 'Ref. Cliente'],
      rightColumn: ['Acumulado', 'Motivo', 'Punto de muestreo', 'Nº muestras']
    },
    preparation: {
      leftColumn: [
        'Analista',
        'Fecha y hora inicio toma de muestra',
        'Fecha y hora fin de toma de muestra',
        'Tipo de Preparación: Evaporación',
        'Cantidad de muestra',
        'Fecha de contaje',
        'cpm brutas de la muestra',
        'Tiempo contaje muestra'
      ],
      rightColumn: [
        'Laboratorio',
        'Unidades',
        'Equipo de contaje',
        'Detector',
        'Procedimiento',
        'Código de fondo alfa',
        'Relación Peso Húmedo/Seco'
      ]
    }
  },

  // ANÁLISIS DE BT-BR (Beta Total - Beta Resto)
  'BT': {
    header: {
      leftColumn: ['Instalación', 'Tipo de muestra', 'Análisis compartido', 'Ref. Cliente'],
      rightColumn: ['Acumulado', 'Motivo', 'Punto de muestreo', 'Nº muestras']
    },
    preparation: {
      leftColumn: [
        'Analista',
        'Fecha y hora inicio toma de muestra',
        'Fecha y hora fin de toma de muestra',
        'Cantidad de muestra',
        'Fecha de contaje',
        'Cpm brutas de la muestra ventana alfa',
        'cpm brutas de la muestra ventana beta',
        'Tiempo contaje muestra',
        'Relación Peso Húmedo/Seco'
      ],
      rightColumn: [
        'Laboratorio',
        'Unidades',
        'Equipo de contaje',
        'Detector',
        'Procedimiento',
        'Código de fondo alfa',
        'Código de fondo beta',
        'Peso precipitado (mg)',
        'mg de potasio (K)'
      ]
    },
    hasDualResults: true
  },

  'BR': {
    header: {
      leftColumn: ['Instalación', 'Tipo de muestra', 'Análisis compartido', 'Ref. Cliente'],
      rightColumn: ['Acumulado', 'Motivo', 'Punto de muestreo', 'Nº muestras']
    },
    preparation: {
      leftColumn: [
        'Analista',
        'Fecha y hora inicio toma de muestra',
        'Fecha y hora fin de toma de muestra',
        'Cantidad de muestra',
        'Fecha de contaje',
        'Cpm brutas de la muestra ventana alfa',
        'cpm brutas de la muestra ventana beta',
        'Tiempo contaje muestra',
        'Relación Peso Húmedo/Seco'
      ],
      rightColumn: [
        'Laboratorio',
        'Unidades',
        'Equipo de contaje',
        'Detector',
        'Procedimiento',
        'Código de fondo alfa',
        'Código de fondo beta',
        'Peso precipitado (mg)',
        'mg de potasio (K)'
      ]
    },
    hasDualResults: true
  },

  // ANÁLISIS DE DOSIMETRÍA AMBIENTAL
  'DP': {
    header: {
      leftColumn: ['Instalación', 'Tipo de muestra', 'Análisis compartido', 'Ref. Cliente'],
      rightColumn: ['Acumulado', 'Motivo', 'Punto de muestreo', 'Nº muestras']
    },
    preparation: {
      leftColumn: [
        'Analista',
        'Fecha y hora inicio toma de muestra',
        'Fecha y hora fin de toma de muestra',
        'Días de exposición',
        'Días de tránsito',
        'Fecha de borrado',
        'Fecha de lectura',
        'Lectura media (µSv)',
        'Error lecturas (µSv)'
      ],
      rightColumn: [
        'Laboratorio',
        'Unidades',
        'Equipo de medida',
        'Procedimiento',
        'Nº de dosímetro',
        'Grupo'
      ]
    }
  },

  // ANÁLISIS DE DOSIMETRÍA AMBIENTAL (alias)
  'DA': {
    header: {
      leftColumn: ['Instalación', 'Tipo de muestra', 'Análisis compartido', 'Ref. Cliente'],
      rightColumn: ['Acumulado', 'Motivo', 'Punto de muestreo', 'Nº muestras']
    },
    preparation: {
      leftColumn: [
        'Analista',
        'Fecha y hora inicio toma de muestra',
        'Fecha y hora fin de toma de muestra',
        'Días de exposición',
        'Días de tránsito',
        'Fecha de borrado',
        'Fecha de lectura',
        'Lectura media (µSv)',
        'Error lecturas (µSv)'
      ],
      rightColumn: [
        'Laboratorio',
        'Unidades',
        'Equipo de medida',
        'Procedimiento',
        'Nº de dosímetro',
        'Grupo'
      ]
    }
  },

  // ANÁLISIS DE ESPECTROMETRÍA ALFA
  'EA': {
    header: {
      leftColumn: ['Instalación', 'Tipo de muestra', 'Análisis compartido', 'Ref. Cliente'],
      rightColumn: ['Acumulado', 'Motivo', 'Punto de muestreo', 'Nº muestras']
    },
    preparation: {
      leftColumn: [
        'Analista',
        'Fecha y hora inicio toma de muestra',
        'Fecha y hora fin de toma de muestra',
        'Cantidad de muestra',
        'Fecha de contaje',
        'Fondo',
        'Volumen trazador',
        'Calibración'
      ],
      rightColumn: [
        'Laboratorio',
        'Unidades',
        'Equipo de medida',
        'Detector',
        'Procedimiento',
        'Tiempo contaje',
        'Certificado trazador',
        'Librería'
      ]
    }
  },

  // ANÁLISIS DE ESPECTROMETRÍA ALFA - Pu238
  'Pu238': {
    header: {
      leftColumn: ['Instalación', 'Tipo de muestra', 'Análisis compartido', 'Ref. Cliente'],
      rightColumn: ['Acumulado', 'Motivo', 'Punto de muestreo', 'Nº muestras']
    },
    preparation: {
      leftColumn: [
        'Analista',
        'Fecha y hora inicio toma de muestra',
        'Fecha y hora fin de toma de muestra',
        'Cantidad de muestra',
        'Fecha de contaje',
        'Fondo',
        'Volumen trazador',
        'Calibración'
      ],
      rightColumn: [
        'Laboratorio',
        'Unidades',
        'Equipo de medida',
        'Detector',
        'Procedimiento',
        'Tiempo contaje',
        'Certificado trazador',
        'Librería'
      ]
    }
  },

  // ANÁLISIS DE ESPECTROMETRÍA ALFA - Pu239+240
  'Pu239+240': {
    header: {
      leftColumn: ['Instalación', 'Tipo de muestra', 'Análisis compartido', 'Ref. Cliente'],
      rightColumn: ['Acumulado', 'Motivo', 'Punto de muestreo', 'Nº muestras']
    },
    preparation: {
      leftColumn: [
        'Analista',
        'Fecha y hora inicio toma de muestra',
        'Fecha y hora fin de toma de muestra',
        'Cantidad de muestra',
        'Fecha de contaje',
        'Fondo',
        'Volumen trazador',
        'Calibración'
      ],
      rightColumn: [
        'Laboratorio',
        'Unidades',
        'Equipo de medida',
        'Detector',
        'Procedimiento',
        'Tiempo contaje',
        'Certificado trazador',
        'Librería'
      ]
    }
  },

  // ANÁLISIS DE ESPECTROMETRÍA ALFA - AM241
  'Am241': {
    header: {
      leftColumn: ['Instalación', 'Tipo de muestra', 'Análisis compartido', 'Ref. Cliente'],
      rightColumn: ['Acumulado', 'Motivo', 'Punto de muestreo', 'Nº muestras']
    },
    preparation: {
      leftColumn: [
        'Analista',
        'Fecha y hora inicio toma de muestra',
        'Fecha y hora fin de toma de muestra',
        'Cantidad de muestra',
        'Fecha de contaje',
        'Fondo',
        'Volumen trazador',
        'Calibración'
      ],
      rightColumn: [
        'Laboratorio',
        'Unidades',
        'Equipo de medida',
        'Detector',
        'Procedimiento',
        'Tiempo contaje',
        'Certificado trazador',
        'Librería'
      ]
    }
  },

  // ANÁLISIS DE ESPECTROMETRÍA ALFA - Cm243+244
  'Cm243+244': {
    header: {
      leftColumn: ['Instalación', 'Tipo de muestra', 'Análisis compartido', 'Ref. Cliente'],
      rightColumn: ['Acumulado', 'Motivo', 'Punto de muestreo', 'Nº muestras']
    },
    preparation: {
      leftColumn: [
        'Analista',
        'Fecha y hora inicio toma de muestra',
        'Fecha y hora fin de toma de muestra',
        'Cantidad de muestra',
        'Fecha de contaje',
        'Fondo',
        'Volumen trazador',
        'Calibración'
      ],
      rightColumn: [
        'Laboratorio',
        'Unidades',
        'Equipo de medida',
        'Detector',
        'Procedimiento',
        'Tiempo contaje',
        'Certificado trazador',
        'Librería'
      ]
    }
  },

  // ANÁLISIS DE ISOTÓPICO GAMMA. ÁREA EXE
  'IG-EXE': {
    header: {
      leftColumn: ['Instalación', 'Tipo de muestra', 'Análisis compartido', 'Ref. Cliente'],
      rightColumn: ['Acumulado', 'Motivo', 'Punto de muestreo', 'Nº muestras']
    },
    preparation: {
      leftColumn: [
        'Analista',
        'Fecha y hora inicio toma de muestra',
        'Fecha y hora fin de toma de muestra',
        'Cantidad de muestra',
        'Fecha de contaje',
        'Geometría',
        'Altura',
        'Decaimiento (min)',
        'Relación Peso Húmedo/Seco'
      ],
      rightColumn: [
        'Laboratorio',
        'Unidades',
        'Equipo de medida',
        'Detector',
        'Procedimiento',
        'Tiempo contaje',
        'Factor Geométrico',
        'Factor de Autoabsorción',
        'Cuentas pico 351 Kev',
        'Cuentas pico 911 Kev'
      ]
    }
  },

  // ANÁLISIS DE ISOTÓPICO GAMMA POR GENIE
  'IG-GENIE': {
    header: {
      leftColumn: ['Instalación', 'Tipo de muestra', 'Análisis compartido', 'Ref. Cliente'],
      rightColumn: ['Acumulado', 'Motivo', 'Punto de muestreo', 'Nº muestras']
    },
    preparation: {
      leftColumn: [
        'Analista',
        'Fecha y hora inicio toma de muestra',
        'Fecha y hora fin de toma de muestra',
        'Cantidad de muestra',
        'Fecha de contaje',
        'Geometría',
        'Altura',
        'Decaimiento (min)',
        'Fondo',
        'Relación Peso Húmedo/Seco'
      ],
      rightColumn: [
        'Laboratorio',
        'Unidades',
        'Equipo de medida',
        'Detector',
        'Procedimiento',
        'Tiempo contaje',
        'Calibración',
        'Librería'
      ]
    }
  },

  // ANÁLISIS DE ISOTÓPICO GAMMA (alias genérico - usar Área Exe por defecto)
  'IG': {
    header: {
      leftColumn: ['Instalación', 'Tipo de muestra', 'Análisis compartido', 'Ref. Cliente'],
      rightColumn: ['Acumulado', 'Motivo', 'Punto de muestreo', 'Nº muestras']
    },
    preparation: {
      leftColumn: [
        'Analista',
        'Fecha y hora inicio toma de muestra',
        'Fecha y hora fin de toma de muestra',
        'Cantidad de muestra',
        'Fecha de contaje',
        'Geometría',
        'Altura',
        'Decaimiento (min)',
        'Relación Peso Húmedo/Seco'
      ],
      rightColumn: [
        'Laboratorio',
        'Unidades',
        'Equipo de medida',
        'Detector',
        'Procedimiento',
        'Tiempo contaje',
        'Factor Geométrico',
        'Factor de Autoabsorción',
        'Cuentas pico 351 Kev',
        'Cuentas pico 911 Kev'
      ]
    }
  },

  // ANÁLISIS DE ISOTÓPICO GAMMA. ÁREA EXE (código legacy)
  'GAMMA-EXE': {
    header: {
      leftColumn: ['Instalación', 'Tipo de muestra', 'Análisis compartido', 'Ref. Cliente'],
      rightColumn: ['Acumulado', 'Motivo', 'Punto de muestreo', 'Nº muestras']
    },
    preparation: {
      leftColumn: [
        'Analista',
        'Fecha y hora inicio toma de muestra',
        'Fecha y hora fin de toma de muestra',
        'Cantidad de muestra',
        'Fecha de contaje',
        'Geometría',
        'Altura',
        'Decaimiento (min)',
        'Relación Peso Húmedo/Seco'
      ],
      rightColumn: [
        'Laboratorio',
        'Unidades',
        'Equipo de medida',
        'Detector',
        'Procedimiento',
        'Tiempo contaje',
        'Factor Geométrico',
        'Factor de Autoabsorción',
        'Cuentas pico 351 Kev',
        'Cuentas pico 911 Kev'
      ]
    }
  },

  // ANÁLISIS DE ISOTÓPICO GAMMA POR GENIE
  'GAMMA-GENIE': {
    header: {
      leftColumn: ['Instalación', 'Tipo de muestra', 'Análisis compartido', 'Ref. Cliente'],
      rightColumn: ['Acumulado', 'Motivo', 'Punto de muestreo', 'Nº muestras']
    },
    preparation: {
      leftColumn: [
        'Analista',
        'Fecha y hora inicio toma de muestra',
        'Fecha y hora fin de toma de muestra',
        'Cantidad de muestra',
        'Fecha de contaje',
        'Geometría',
        'Altura',
        'Fondo',
        'Relación Peso Húmedo/Seco'
      ],
      rightColumn: [
        'Laboratorio',
        'Unidades',
        'Equipo de medida',
        'Detector',
        'Procedimiento',
        'Tiempo contaje',
        'Calibración',
        'Librería'
      ]
    }
  }
};

/**
 * Layout por defecto para análisis que no tienen configuración específica
 */
export const defaultLayout: AnalysisLayout = {
  header: {
    leftColumn: ['Instalación', 'Tipo de muestra', 'Análisis compartido'],
    rightColumn: ['Acumulado', 'Motivo', 'Punto de muestreo']
  },
  preparation: {
    leftColumn: [
      'Analista',
      'Fecha y hora inicio toma de muestra',
      'Fecha y hora fin de toma de muestra',
      'Cantidad de muestra',
      'Fecha de contaje',
      'cpm brutas muestra',
      'cpm brutas blanco',
      'Tiempo contaje muestra'
    ],
    rightColumn: [
      'Laboratorio',
      'Unidades',
      'Equipo de contaje',
      'Procedimiento'
    ]
  }
};

/**
 * Obtiene el layout para un tipo de análisis específico
 * @param analysisCode - Código del tipo de análisis
 * @returns Layout configurado o layout por defecto
 */
export const getAnalysisLayout = (analysisCode: string): AnalysisLayout => {
  return analysisLayouts[analysisCode] || defaultLayout;
};
